import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");
const ord = (d) => `${d}${d % 10 === 1 && d !== 11 ? "st" : d % 10 === 2 && d !== 12 ? "nd" : d % 10 === 3 && d !== 13 ? "rd" : "th"}`;

function OwnerEditor({ item, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [owner, setOwner] = useState(item.owner);
  const [email, setEmail] = useState(item.owner_email || "");
  const save = async () => {
    try {
      await axios.put(`${API}/cadence/owners`, { key: item.key, owner, owner_email: email });
      toast.success(`Owner for "${item.title}" saved`);
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save owner");
    }
  };
  if (!editing) {
    return (
      <span>
        <b>{item.owner}</b>{item.owner_email ? <span className="cap-sub"> {item.owner_email}</span> : <span className="cap-sub" style={{ color: "var(--red)" }}> no email set — owner emails fall back to the full team</span>}
        {" "}<button className="vr-link" onClick={() => setEditing(true)} data-testid={`cadence-owner-edit-${item.key}`}>edit</button>
      </span>
    );
  }
  return (
    <span className="vr-form" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      <input className="inp" style={{ maxWidth: 180 }} value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="Owner (person)" data-testid={`cadence-owner-name-${item.key}`} />
      <input className="inp" style={{ maxWidth: 220 }} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="owner@email.com" data-testid={`cadence-owner-email-${item.key}`} />
      <button className="btn" onClick={save} data-testid={`cadence-owner-save-${item.key}`}>Save</button>
      <button className="btn ghost" onClick={() => setEditing(false)}>Cancel</button>
    </span>
  );
}

export default function CadenceBoard({ active, show }) {
  const [data, setData] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    axios.get(`${API}/cadence`).then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (active === "cad") load(); }, [active, load]);

  const downloadIcs = async (key) => {
    try {
      const res = await axios.get(`${API}/cadence/ics${key ? `?key=${key}` : ""}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `ech_cadence_${key || "all"}.ics`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Could not build the calendar file");
    }
  };

  const sendTests = async () => {
    if (!testTo.trim()) { toast.error("Enter the recipient email first"); return; }
    setBusy("test");
    try {
      const res = await axios.post(`${API}/cadence/test-emails`, { to: testTo });
      toast.success(`${res.data.sent} template tests ${res.data.mode === "PREVIEW" ? "generated in PREVIEW (not delivered — no Resend key)" : "sent"} to ${res.data.to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test send failed");
    }
    setBusy("");
  };

  const runNow = async () => {
    setBusy("run");
    try {
      const res = await axios.post(`${API}/cadence/run-now`);
      toast.success(`Engine run: ${res.data.caps_created} CAP(s) auto-opened, ${res.data.emails} email(s) ${res.data.mode === "PREVIEW" ? "previewed (NOT delivered)" : "sent"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Run failed");
    }
    setBusy("");
  };

  const entries = data?.entries || [];
  const overdue = entries.filter((e) => e.status === "OVERDUE");

  return (
    <section className={`chapter utility${active === "cad" ? " active" : ""}`} id="cad">
      <div className="eyebrow">Working record · the monthly operating cadence — live Sept 1, owner-named deadlines with auto-CAP on miss</div>
      <h1>Monthly Cadence</h1>
      <p className="lede">Five recurring deadlines plus the monthly master-file upload, each with exactly one named owner. The engine watches the underlying records — a deadline clears only when the actual work is logged, and every completion timestamp is server-set at the moment of the action. A missed deadline auto-opens a CAP that stays open until the owner documents root cause and corrective action. Late and honest is the required behavior; backdating is impossible by design.</p>

      {data && data.email_mode === "PREVIEW" && (
        <div className="pc-decision decline" data-testid="cadence-preview-warning">
          EMAILS ARE NOT BEING DELIVERED — no Resend API key on file. Reminders are generated to the outbox (Email Center) only. Add <span className="q">RESEND_API_KEY</span> to go live before Sept 1.
        </div>
      )}

      <div className="formcard" data-testid="cadence-config">
        <div className="formcard-title">The six deadlines — owners editable, one person each (not a lane)</div>
        <table className="mform-table"><tbody>
          <tr><th style={{ width: 50 }}>Day</th><th>Deadline</th><th style={{ width: 320 }}>Owner (person + email)</th><th style={{ width: 130 }}>Calendar</th></tr>
          {(data?.items || []).map((it) => (
            <tr key={it.key}>
              <td className="q">{ord(it.day)}</td>
              <td>{it.title}</td>
              <td><OwnerEditor item={it} onSaved={load} /></td>
              <td><button className="vr-link" onClick={() => downloadIcs(it.key)} data-testid={`cadence-ics-${it.key}`}>＋ Add to calendar</button></td>
            </tr>
          ))}
        </tbody></table>
        <div className="vr-btnrow" style={{ marginTop: 10 }}>
          <button className="btn" onClick={() => downloadIcs("")} data-testid="cadence-ics-all">Add all five to calendar (.ics, monthly recurring)</button>
          <button className="btn ghost" onClick={runNow} disabled={!!busy} data-testid="cadence-run-now">{busy === "run" ? "Running…" : "Run engine now (emails + auto-CAP sweep)"}</button>
        </div>
      </div>

      <div className="formcard" data-testid="cadence-email-card">
        <div className="formcard-title">Email schedule per deadline</div>
        <table className="mform-table"><tbody>
          <tr><th>When</th><th>Who receives it</th></tr>
          <tr><td>T-2 days, 8:00 AM ET</td><td>Owner only</td></tr>
          <tr><td>Due date, 8:00 AM ET</td><td>Owner only</td></tr>
          <tr><td>Overdue — daily 8:00 AM ET until actual completion</td><td>Owner + full team (escalation names the owner)</td></tr>
        </tbody></table>
        <div className="vr-btnrow" style={{ marginTop: 10, alignItems: "center" }}>
          <input className="inp" style={{ maxWidth: 260 }} placeholder="recipient@email.com" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="cadence-test-email-input" />
          <button className="btn" onClick={sendTests} disabled={!!busy} data-testid="cadence-test-email-btn">{busy === "test" ? "Sending…" : "Send a test of each template"}</button>
        </div>
        <p className="k-sub" style={{ marginTop: 8 }}>Sends all four templates (T-2 · due today · overdue owner · overdue team escalation) to the address above. Delivery status lands in the <button className="vr-link" onClick={() => show("mail")}>Email Center outbox</button>.</p>
      </div>

      <h2>Occurrence history {overdue.length ? `— ${overdue.length} overdue` : ""}</h2>
      <table data-testid="cadence-history-table"><tbody>
        <tr><th>Month</th><th>Deadline</th><th>Owner</th><th>Due</th><th>Period covered</th><th>Status</th><th>Actual completion</th></tr>
        {entries.length === 0 ? (
          <tr><td colSpan={7}>Loading…</td></tr>
        ) : ([...entries].reverse().map((e) => (
          <tr key={`${e.key}-${e.month}`}>
            <td className="q">{e.month}</td>
            <td>{e.title}</td>
            <td>{e.owner}</td>
            <td>{e.due}</td>
            <td>{e.period}</td>
            <td>
              {e.status === "DONE" && !e.late && <span className="vr-pill cleared">DONE</span>}
              {e.status === "DONE" && e.late && <span className="vr-pill open">DONE LATE — honest record</span>}
              {e.status === "OVERDUE" && <span className="vr-pill declined">OVERDUE {-e.days}d — {e.owner}</span>}
              {e.status === "DUE_TODAY" && <span className="vr-pill open">DUE TODAY</span>}
              {e.status === "UPCOMING" && <span className="vr-pill open">in {e.days}d</span>}
            </td>
            <td>{e.completed_at ? fmt(e.completed_at) : "—"}</td>
          </tr>
        )))}
      </tbody></table>
      <p style={{ color: "var(--mute)", fontSize: "13px" }}>Completion is derived from the record of the actual work (Alteva report upload · A-8 screening log · MOCR attestations · MOCR close · lender-report filing) — there is no "mark done" button and no way to backdate. Overdue items clear only through actual completion; the auto-CAP additionally requires root cause + corrective action in the <button className="vr-link" onClick={() => show("caps")}>CAP Register</button>.</p>
    </section>
  );
}
